import { useId, useState } from 'react'
import { Link } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import type { Account } from '@/features/auth/account'
import { errorMessage, unwrap } from '@/lib/errors'
import { qk } from '@/lib/query-keys'
import { site } from '@/lib/site'
import { supabase } from '@/lib/supabase'

export type ConsentKind = 'vote' | 'publish'

const COPY: Record<ConsentKind, { title: string; intro: string; points: string[]; statement: string }> = {
  vote: {
    title: 'Consentimento para registrar seus votos',
    intro:
      'Um voto sobre uma proposta revela uma opinião política, que a Lei Geral de Proteção de Dados considera dado pessoal sensível (art. 5º, II). Por isso, precisamos do seu consentimento específico (art. 11, I).',
    points: [
      'Seu voto individual não é exibido para ninguém: nem para outros participantes, nem para a moderação. Publicamos apenas totais.',
      'A divisão entre Aprovo, Neutro e Rejeito só aparece quando uma proposta tem pelo menos 5 votos, para proteger o sigilo de cada voto.',
      'O mesmo consentimento vale para marcar comentários como úteis e apoiar sugestões de alteração.',
      'Você pode retirar o consentimento quando quiser, na página da sua conta. Ao retirar, seus votos e apoios são apagados.',
    ],
    statement:
      'Consinto com o tratamento dos meus votos e apoios para compor os resultados consultivos do Poder Aberto, nos termos da Política de Privacidade.',
  },
  publish: {
    title: 'Consentimento para publicar',
    intro:
      'Comentários, sugestões e propostas são públicos e aparecem com o seu nome público, que pode ser um pseudônimo. Como podem revelar opiniões políticas (dado pessoal sensível pela LGPD), pedimos seu consentimento específico.',
    points: [
      'Não inclua dados pessoais seus ou de outras pessoas, como telefone, endereço ou documentos.',
      'Você pode excluir cada contribuição quando quiser. Se excluir a conta, o texto dos seus comentários e sugestões é apagado.',
      'Você pode retirar este consentimento na página da sua conta. As contribuições já publicadas continuam até que você as exclua.',
    ],
    statement:
      'Consinto com a publicação das minhas contribuições, vinculadas ao meu nome público, nos termos dos Termos de Uso e da Política de Privacidade.',
  },
}

type ConsentDialogProps = {
  kind: ConsentKind | null
  onClose: (granted: boolean) => void
}

export function ConsentDialog({ kind, onClose }: ConsentDialogProps) {
  return (
    <Dialog open={kind !== null} onOpenChange={(open) => !open && onClose(false)}>
      {kind ? <ConsentDialogBody key={kind} kind={kind} onClose={onClose} /> : null}
    </Dialog>
  )
}

function ConsentDialogBody({ kind, onClose }: { kind: ConsentKind; onClose: (granted: boolean) => void }) {
  const copy = COPY[kind]
  const checkboxId = useId()
  const [checked, setChecked] = useState(false)
  const queryClient = useQueryClient()

  const grant = useMutation({
    mutationFn: async () => {
      const result =
        kind === 'vote'
          ? ((unwrap(await supabase.rpc('set_vote_consent', { p_granted: true, p_version: site.versions.voteConsent })) as unknown as {
              account: Account
            }).account)
          : (unwrap(
              await supabase.rpc('set_publication_consent', { p_granted: true, p_version: site.versions.publicationConsent }),
            ) as unknown as Account)
      return result
    },
    onSuccess: (account) => {
      queryClient.setQueryData(qk.me.account, account)
      onClose(true)
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{copy.title}</DialogTitle>
        <DialogDescription className="leading-relaxed">{copy.intro}</DialogDescription>
      </DialogHeader>
      <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-foreground marker:text-muted-foreground">
        {copy.points.map((point) => (
          <li key={point}>{point}</li>
        ))}
      </ul>
      <div className="flex items-start gap-3 rounded-md border border-border bg-subtle p-3">
        <Checkbox
          id={checkboxId}
          checked={checked}
          onCheckedChange={(value) => setChecked(value === true)}
          className="mt-0.5"
        />
        <label htmlFor={checkboxId} className="text-sm leading-relaxed">
          {copy.statement}{' '}
          <Link to="/privacidade" target="_blank" className="font-medium text-primary underline underline-offset-2">
            Ler a Política de Privacidade
          </Link>
        </label>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)} disabled={grant.isPending}>
          Agora não
        </Button>
        <Button onClick={() => grant.mutate()} disabled={!checked || grant.isPending}>
          {grant.isPending ? <Spinner aria-hidden="true" /> : null}
          Consentir e continuar
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}
